<script lang="ts">
  import Icon from "./Icon.svelte";
  import Autocomplete from "./Autocomplete.svelte";
  import {
    parseCommand,
    getMatchingCommands,
    getMatchingSubcommands,
    COMMANDS,
    WORKFLOWS,
    MOCK_TOKENS,
    type Command,
    type Subcommand,
    type Workflow,
    type SearchResult,
  } from "../lib/commands";

  interface Props {
    value: string;
    onSearch: (query: string) => void;
    onClear: () => void;
  }

  let { value = $bindable(), onSearch, onClear }: Props = $props();

  let inputElement: HTMLInputElement;
  let showAutocomplete = $state(false);
  let selectedIndex = $state(0);
  let isFocused = $state(false);

  // Computed autocomplete items
  let autocompleteItems = $derived.by(() => {
    if (!value) return [];

    const items: Array<{
      type: "command" | "subcommand" | "workflow" | "token";
      data: Command | Subcommand | Workflow | SearchResult;
      command?: string;
    }> = [];

    if (value.startsWith("/")) {
      const { command, subcommand } = parseCommand(value);

      if (!command || command === "") {
        // Show all commands
        COMMANDS.forEach((cmd) => items.push({ type: "command", data: cmd }));
      } else {
        const matchingCommands = getMatchingCommands(value);

        if (matchingCommands.length === 1 && matchingCommands[0].name === command) {
          // Exact command match, show subcommands
          const subs = getMatchingSubcommands(value);
          subs.forEach((sub) =>
            items.push({ type: "subcommand", data: sub, command })
          );
        } else {
          // Partial command match
          matchingCommands.forEach((cmd) => items.push({ type: "command", data: cmd }));
        }
      }

      // Also show matching workflows
      const searchTerm = value.toLowerCase().slice(1);
      WORKFLOWS.filter(
        (w) =>
          w.name.toLowerCase().includes(searchTerm) ||
          w.command.toLowerCase().includes(searchTerm)
      ).forEach((w) => items.push({ type: "workflow", data: w }));
    } else if (value.length >= 2) {
      // Search tokens, addresses, etc.
      const searchTerm = value.toLowerCase();

      MOCK_TOKENS.filter(
        (t) =>
          t.name.toLowerCase().includes(searchTerm) ||
          t.address?.toLowerCase().includes(searchTerm)
      ).forEach((t) => items.push({ type: "token", data: t }));

      // Show relevant workflows
      WORKFLOWS.filter((w) =>
        w.name.toLowerCase().includes(searchTerm)
      ).forEach((w) => items.push({ type: "workflow", data: w }));
    }

    return items;
  });

  function handleInput(e: Event) {
    const target = e.target as HTMLInputElement;
    value = target.value;
    selectedIndex = 0;
    showAutocomplete = value.length > 0;
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (!showAutocomplete || autocompleteItems.length === 0) {
      if (e.key === "Enter") {
        onSearch(value);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        selectedIndex = (selectedIndex + 1) % autocompleteItems.length;
        break;
      case "ArrowUp":
        e.preventDefault();
        selectedIndex = (selectedIndex - 1 + autocompleteItems.length) % autocompleteItems.length;
        break;
      case "Enter":
        e.preventDefault();
        selectItem(autocompleteItems[selectedIndex]);
        break;
      case "Escape":
        showAutocomplete = false;
        break;
      case "Tab":
        if (autocompleteItems.length > 0) {
          e.preventDefault();
          selectItem(autocompleteItems[selectedIndex]);
        }
        break;
    }
  }

  function selectItem(item: typeof autocompleteItems[0]) {
    if (item.type === "command") {
      const cmd = item.data as Command;
      value = `/${cmd.name} `;
      showAutocomplete = true;
      selectedIndex = 0;
    } else if (item.type === "subcommand") {
      const sub = item.data as Subcommand;
      value = `/${item.command} ${sub.name} `;
      if (!sub.placeholder) {
        onSearch(value.trim());
        showAutocomplete = false;
      }
    } else if (item.type === "workflow") {
      const workflow = item.data as Workflow;
      value = workflow.command;
      onSearch(value);
      showAutocomplete = false;
    } else if (item.type === "token") {
      const token = item.data as SearchResult;
      value = token.address || token.name;
      onSearch(value);
      showAutocomplete = false;
    }

    inputElement?.focus();
  }

  function handleFocus() {
    isFocused = true;
    if (value) showAutocomplete = true;
  }

  function handleBlur() {
    isFocused = false;
    // Delay hiding to allow click on autocomplete
    setTimeout(() => {
      showAutocomplete = false;
    }, 200);
  }

  function handleClear() {
    value = "";
    showAutocomplete = false;
    onClear();
    inputElement?.focus();
  }

  function handleSubmit(e: Event) {
    e.preventDefault();
    onSearch(value);
    showAutocomplete = false;
  }

  // Keyboard shortcut to focus search
  $effect(() => {
    function handleGlobalKeydown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputElement?.focus();
      }
    }

    window.addEventListener("keydown", handleGlobalKeydown);
    return () => window.removeEventListener("keydown", handleGlobalKeydown);
  });
</script>

<div class="search-container">
  <form class="search-form" onsubmit={handleSubmit}>
    <div class="search-box" class:focused={isFocused}>
      <div class="search-icon">
        <Icon name="search" size={20} />
      </div>

      <input
        bind:this={inputElement}
        type="text"
        class="search-input"
        placeholder="Search address, ENS, tx hash, or type / for commands..."
        {value}
        oninput={handleInput}
        onkeydown={handleKeyDown}
        onfocus={handleFocus}
        onblur={handleBlur}
        autocomplete="off"
        spellcheck="false"
      />

      <div class="search-actions">
        {#if value}
          <button type="button" class="clear-button" onclick={handleClear}>
            <Icon name="x" size={16} />
          </button>
        {/if}

        <kbd class="shortcut">
          <Icon name="command" size={12} />
          <span>K</span>
        </kbd>

        <button type="submit" class="submit-button" disabled={!value.trim()}>
          Search
          <Icon name="arrow-right" size={16} />
        </button>
      </div>
    </div>
  </form>

  {#if showAutocomplete && autocompleteItems.length > 0}
    <Autocomplete
      items={autocompleteItems}
      {selectedIndex}
      onSelect={selectItem}
    />
  {/if}

  <div class="search-hints">
    <span class="hint">
      <kbd>/</kbd> commands
    </span>
    <span class="hint">
      <kbd>↑↓</kbd> navigate
    </span>
    <span class="hint">
      <kbd>↵</kbd> select
    </span>
    <span class="hint">
      <kbd>esc</kbd> close
    </span>
  </div>
</div>

<style>
  .search-container {
    position: relative;
    width: 100%;
  }

  .search-form {
    width: 100%;
  }

  .search-box {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--bg-secondary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-xl);
    transition: all var(--transition-base);
  }

  .search-box:hover {
    border-color: var(--border-default);
  }

  .search-box.focused {
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1), var(--shadow-lg);
  }

  .search-icon {
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .search-input {
    flex: 1;
    min-width: 0;
    font-size: 1rem;
    font-family: var(--font-sans);
    background: transparent;
    border: none;
    outline: none;
    color: var(--text-primary);
  }

  .search-input::placeholder {
    color: var(--text-muted);
  }

  .search-actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .clear-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--radius-md);
    color: var(--text-tertiary);
    transition: all var(--transition-fast);
  }

  .clear-button:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  .shortcut {
    display: none;
    align-items: center;
    gap: 2px;
    padding: var(--space-1) var(--space-2);
    font-size: 0.75rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
  }

  @media (min-width: 640px) {
    .shortcut {
      display: flex;
    }
  }

  .submit-button {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
    background: var(--text-primary);
    color: var(--bg-primary);
    border-radius: var(--radius-md);
    font-size: 0.875rem;
    font-weight: 500;
    transition: all var(--transition-fast);
  }

  .submit-button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  .submit-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .search-hints {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    margin-top: var(--space-3);
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .hint {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }

  .hint kbd {
    font-size: 0.6875rem;
    padding: 2px 4px;
  }

  @media (max-width: 480px) {
    .search-hints {
      display: none;
    }
  }
</style>
